export const scalarProd = (v, w) => {
    return v[0] * w[0] + v[1] * w[1];
};
export const vectorAdd = (v, w) => {
    return [v[0] + w[0], v[1] + w[1]];
};
export const vectorSubtract = (v, w) => {
    return [v[0] - w[0], v[1] - w[1]];
};
export const project = (point, V, W) => {
    return [scalarProd(point, V), scalarProd(point, W)];
};
export const transform = (point, V, W) => {
    return [V[0] * point[0] + W[0] * point[1], V[1] * point[0] + W[1] * point[1]];
};
const alignVectorToGrid = (direction, xAxis, yAxis) => {
    const scalars = [scalarProd(xAxis, direction), scalarProd(yAxis, direction)];
    let newDirection = xAxis;
    if (Math.abs(scalars[1]) > Math.abs(scalars[0])) {
        newDirection = yAxis;
    }
    if (scalarProd(newDirection, direction) < 0) {
        newDirection = [-newDirection[0], -newDirection[1]];
    }
    return newDirection;
};
const returnLongest = (vector1, vector2) => {
    if (scalarProd(vector1, vector1) >= scalarProd(vector2, vector2)) {
        return vector1;
    }
    return vector2;
};
export function parseCeilingGrids(ceilingGrids, point) {
    const allBoundaries = ceilingGrids.map((grid) => {
        const { edgeLines, interiorLines } = edgesAndInteriorLines(grid.lines);
        return edgeLines;
    });
    const indexNearest = getClosestIndex(point, allBoundaries);
    if (indexNearest === -1)
        return null;
    const { edgeLines: nearestEdgeLines, interiorLines: nearestInteriorLines } = edgesAndInteriorLines(ceilingGrids[indexNearest].lines);
    const subgrid = parseOneCeilingGrid(nearestEdgeLines, nearestInteriorLines);
    return subgrid;
}
function getClosestIndex(point, boundaryArray) {
    for (let i = 0; i < boundaryArray.length; i++) {
        if (isInside(point, boundaryArray[i]))
            return i;
    }
    return -1;
}
function parseOneCeilingGrid(edgeLines, internalLines) {
    const [xAxis, yAxis] = getGridAxes(internalLines);
    const edgeLinesLocal = toLocal(edgeLines, xAxis, yAxis);
    const interiorLinesLocal = toLocal(internalLines, xAxis, yAxis);
    const { xLines: xLinesEdge, yLines: yLinesEdge } = arrangeLocalLines(edgeLinesLocal);
    const { xLines: xLinesInterior, yLines: yLinesInterior } = arrangeLocalLines(interiorLinesLocal);
    const bboxs = getAllBoundingBoxes(xLinesInterior, yLinesInterior, xLinesEdge, yLinesEdge);
    const tiles = bboxs.map((bboxRow) => bboxRow.map((bbox) => bbox2tile(bbox, xAxis, yAxis)));
    return { internalLines, edgeLines, xAxis, yAxis, tiles };
}
function bbox2tile(bbox, xAxis, yAxis) {
    if (!Number.isFinite(bbox.minX) ||
        !Number.isFinite(bbox.maxX) ||
        !Number.isFinite(bbox.minY) ||
        !Number.isFinite(bbox.maxY))
        return { center: undefined, boundingBox: undefined };
    const center = transform([(bbox.minX + bbox.maxX) / 2, (bbox.minY + bbox.maxY) / 2], xAxis, yAxis);
    const boundingBox = [
        [bbox.minX, bbox.minY],
        [bbox.maxX, bbox.minY],
        [bbox.maxX, bbox.maxY],
        [bbox.minX, bbox.maxY],
        [bbox.minX, bbox.minY],
    ].map((point) => transform(point, xAxis, yAxis));
    return { center, boundingBox };
}
const DEFAULT_CEILING_TILE_SIZE_M = 0.6;
const DEFAULT_TILE_OVERSIZE_TOLERANCE = 0.4;
const estimateTileSpan = (length, tileSizeMeters, oversizeToleranceInTiles) => {
    if (!Number.isFinite(length) || length <= 0)
        return 1;
    const rawTileSpan = length / tileSizeMeters;
    return Math.max(1, Math.ceil(rawTileSpan - oversizeToleranceInTiles));
};
export const calculateTileSizeFromBoundingBox = (boundingBox, options) => {
    const tileSizeMeters = options?.tileSizeMeters ?? DEFAULT_CEILING_TILE_SIZE_M;
    const oversizeToleranceInTiles = options?.oversizeToleranceInTiles ?? DEFAULT_TILE_OVERSIZE_TOLERANCE;
    const footprint = boundingBox
        .filter((point) => point.length >= 2 && Number.isFinite(point[0]) && Number.isFinite(point[1]))
        .map((point) => [point[0], point[1]]);
    if (footprint.length < 2) {
        return [1, 1];
    }
    const uniqueFootprint = footprint.filter((point, index, arr) => arr.findIndex((candidate) => candidate[0] === point[0] && candidate[1] === point[1]) === index);
    if (uniqueFootprint.length < 2) {
        return [1, 1];
    }
    const closedFootprint = uniqueFootprint.length > 2 &&
        (uniqueFootprint[0][0] !== uniqueFootprint[uniqueFootprint.length - 1][0] ||
            uniqueFootprint[0][1] !== uniqueFootprint[uniqueFootprint.length - 1][1])
        ? [...uniqueFootprint, uniqueFootprint[0]]
        : uniqueFootprint;
    const polyBox = getPolygonCoordinateSystem(closedFootprint);
    const localFootprint = getLocalPolygon(closedFootprint, polyBox);
    const xValues = localFootprint.map((point) => point[0]);
    const yValues = localFootprint.map((point) => point[1]);
    const width = Math.max(...xValues) - Math.min(...xValues);
    const height = Math.max(...yValues) - Math.min(...yValues);
    const tileSpan = [
        estimateTileSpan(width, tileSizeMeters, oversizeToleranceInTiles),
        estimateTileSpan(height, tileSizeMeters, oversizeToleranceInTiles),
    ];
    return tileSpan[0] <= tileSpan[1] ? tileSpan : [tileSpan[1], tileSpan[0]];
};
export const adjustObject = (obj, ceilingGrids) => {
    const newObj = { ...obj };
    const subgrid = parseCeilingGrids(ceilingGrids, newObj.center);
    if (subgrid === null)
        return newObj;
    // Align direction first so sub-tile layout uses the correct axes
    let alignedDirection;
    if (newObj.direction && newObj.direction.length > 1) {
        alignedDirection = alignVectorToGrid(newObj.direction, subgrid.xAxis, subgrid.yAxis);
        newObj.direction = alignedDirection;
    }
    const tileSize = newObj.tileSize ?? [1, 1];
    // Long axis = tileSize[1] (largeSpan), short axis = tileSize[0]
    const longSpan = Math.max(tileSize[0], tileSize[1]);
    const shortSpan = Math.min(tileSize[0], tileSize[1]);
    // Sub-tile centers at 0.6m spacing — same geometry as the drawn rectangle
    let devX = subgrid.xAxis;
    let devY = subgrid.yAxis;
    if (alignedDirection) {
        devX = alignedDirection;
        devY = [-alignedDirection[1], alignedDirection[0]];
    }
    const subTileCenters = getSubTileCenters(newObj.center, [longSpan, shortSpan], [DEFAULT_CEILING_TILE_SIZE_M, DEFAULT_CEILING_TILE_SIZE_M], devX, devY);
    // For each sub-tile center find its nearest tile
    const matchedTiles = subTileCenters.map((pt) => getGridTile(subgrid.tiles, pt));
    // Validate: all snap offsets (sub-tile center → tile center) should be roughly equal
    const offsets = matchedTiles.map((tile, i) => tile?.center != null ? vectorSubtract(tile.center, subTileCenters[i]) : null);
    const allValid = offsets.every((o) => o != null);
    if (!allValid)
        return newObj;
    const refOffset = offsets[0];
    const consistent = offsets.every((o) => {
        const diff = vectorSubtract(o, refOffset);
        return Math.sqrt(scalarProd(diff, diff)) < DEFAULT_CEILING_TILE_SIZE_M * 0.5;
    });
    if (!consistent)
        return newObj;
    // Compute center of the union of matched tiles' bounding boxes
    const allBBoxPoints = matchedTiles.flatMap((tile) => tile?.boundingBox ?? []);
    if (allBBoxPoints.length === 0)
        return newObj;
    const xs = allBBoxPoints.map((p) => p[0]);
    const ys = allBBoxPoints.map((p) => p[1]);
    const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
    const centerY = (Math.min(...ys) + Math.max(...ys)) / 2;
    newObj.center = [centerX, centerY];
    return newObj;
};
export function snapPointToNearestTileCenter(point, ceilingGrids) {
    const subgrid = parseCeilingGrids(ceilingGrids, point);
    if (subgrid == null)
        return point;
    const tile = getGridTile(subgrid.tiles, point);
    return tile?.center ?? point;
}
export function getTilePitch(tiles) {
    let pitchX = 0, pitchY = 0;
    // Pitch along rows (x direction in tile grid)
    for (const row of tiles) {
        for (let j = 1; j < row.length; j++) {
            if (row[j].center && row[j - 1].center) {
                const d = vectorSubtract(row[j].center, row[j - 1].center);
                pitchX = Math.sqrt(scalarProd(d, d));
                break;
            }
        }
        if (pitchX > 0)
            break;
    }
    // Pitch along columns (y direction in tile grid)
    for (let i = 1; i < tiles.length; i++) {
        const col = tiles[i];
        const prevCol = tiles[i - 1];
        for (let j = 0; j < col.length; j++) {
            if (col[j].center && prevCol[j]?.center) {
                const d = vectorSubtract(col[j].center, prevCol[j].center);
                pitchY = Math.sqrt(scalarProd(d, d));
                break;
            }
        }
        if (pitchY > 0)
            break;
    }
    return [pitchX || pitchY, pitchY || pitchX];
}
function getSubTileCenters(center, tileSize, pitch, xAxis, yAxis) {
    const [nx, ny] = tileSize;
    const centers = [];
    for (let i = 0; i < nx; i++) {
        for (let j = 0; j < ny; j++) {
            const offsetX = (i - (nx - 1) / 2) * pitch[0];
            const offsetY = (j - (ny - 1) / 2) * pitch[1];
            const worldOffset = vectorAdd([xAxis[0] * offsetX, xAxis[1] * offsetX], [yAxis[0] * offsetY, yAxis[1] * offsetY]);
            centers.push(vectorAdd(center, worldOffset));
        }
    }
    return centers;
}
function getGridTile(tiles, point) {
    let nearestTile = null;
    let minDistance = Infinity;
    for (let tileRow of tiles) {
        for (let tile of tileRow) {
            if (tile.center !== undefined) {
                const delta = vectorSubtract(tile.center, point);
                const prod = scalarProd(delta, delta);
                if (prod < minDistance) {
                    minDistance = prod;
                    nearestTile = tile;
                }
            }
        }
    }
    return nearestTile;
}
function isInside(point, lines) {
    const [px, py] = point;
    let inside = false;
    for (const [[x1, y1], [x2, y2]] of lines) {
        // Check if the edge crosses the horizontal ray to +inf from point
        const intersects = y1 > py !== y2 > py && px < ((x2 - x1) * (py - y1)) / (y2 - y1) + x1;
        if (intersects)
            inside = !inside;
    }
    return inside;
}
/**
 * Logic for identifying all tiles
 * - If no internal lines
 *    - if external lines: only one bbox given by externalLines
 * - If internal lines
 *    - all 4 bboxes around intersection between
 */
function getAllBoundingBoxes(xLinesInterior, yLinesInterior, xLinesEdge, yLinesEdge) {
    const xVals = [-Infinity, ...getAxesVals(yLinesInterior, "x"), Infinity];
    const yVals = [-Infinity, ...getAxesVals(xLinesInterior, "y"), Infinity];
    const xLines = [...xLinesInterior, ...xLinesEdge];
    const yLines = [...yLinesInterior, ...yLinesEdge];
    const epsilon = 1e-3;
    const nX = xVals.length - 1;
    const nY = yVals.length - 1;
    const bboxs = Array.from({ length: nX }, () => Array.from({ length: nY }, () => ({
        minX: Infinity,
        maxX: -Infinity,
        minY: Infinity,
        maxY: -Infinity,
    })));
    for (let yLine of yLines) {
        for (let xLine of xLines) {
            const { a, b, x, y } = getIntersectionAlongLines(xLine, yLine);
            if (-epsilon < a && a < 1 + epsilon && -epsilon < b && b < 1 + epsilon) {
                // Lines intersect, or almost intersect
                // Get which x and y interval intersection is in.
                const { lo: ix0, hi: ix1 } = binarySearchRange(xVals, x);
                const { lo: iy0, hi: iy1 } = binarySearchRange(yVals, y);
                const indexLeft = Math.abs(xVals[ix0] - x) < epsilon ? ix0 - 1 : ix0;
                const indexRight = Math.abs(xVals[ix1] - x) < epsilon ? ix1 : ix0;
                const indexBelow = Math.abs(yVals[iy0] - y) < epsilon ? iy0 - 1 : iy0;
                const indexAbove = Math.abs(yVals[iy1] - y) < epsilon ? iy1 : iy0;
                const space2Left = a > epsilon;
                const space2Right = a < 1 - epsilon;
                const spaceBelow = b > epsilon;
                const spaceAbove = b < 1 - epsilon;
                if (space2Left && spaceBelow) {
                    bboxs[indexLeft][indexBelow].maxX = Math.max(bboxs[indexLeft][indexBelow].maxX, x);
                    bboxs[indexLeft][indexBelow].maxY = Math.max(bboxs[indexLeft][indexBelow].maxY, y);
                }
                if (space2Left && spaceAbove) {
                    bboxs[indexLeft][indexAbove].maxX = Math.max(bboxs[indexLeft][indexAbove].maxX, x);
                    bboxs[indexLeft][indexAbove].minY = Math.min(bboxs[indexLeft][indexAbove].minY, y);
                }
                if (space2Right && spaceBelow) {
                    bboxs[indexRight][indexBelow].minX = Math.min(bboxs[indexRight][indexBelow].minX, x);
                    bboxs[indexRight][indexBelow].maxY = Math.max(bboxs[indexRight][indexBelow].maxY, y);
                }
                if (space2Right && spaceAbove) {
                    bboxs[indexRight][indexAbove].minX = Math.min(bboxs[indexRight][indexAbove].minX, x);
                    bboxs[indexRight][indexAbove].minY = Math.min(bboxs[indexRight][indexAbove].minY, y);
                }
            }
        }
    }
    return bboxs;
}
/**
 * Get intersection point between two lines, and also return a and b, which are the fractional position along each line (0 at start, 1 at end)
 * Only works for lines that are aligned with x and y axes.
 */
function getIntersectionAlongLines(xLine, yLine) {
    const x = yLine[0][0];
    const y = xLine[0][1];
    const a = (x - xLine[0][0]) / (xLine[1][0] - xLine[0][0]);
    const b = (y - yLine[0][1]) / (yLine[1][1] - yLine[0][1]);
    return { a, b, x, y };
}
function binarySearchRange(arr, x) {
    const n = arr.length;
    // hi = first index with arr[i] >= x
    let loHi = 0, hiHi = n;
    while (loHi < hiHi) {
        const mid = (loHi + hiHi) >> 1;
        if (arr[mid] < x)
            loHi = mid + 1;
        else
            hiHi = mid;
    }
    const hi = loHi; // may be n
    // lo = last index with arr[i] <= x
    let loLo = -1, hiLo = n - 1;
    while (loLo < hiLo) {
        const mid = (loLo + hiLo + 1) >> 1;
        if (arr[mid] > x)
            hiLo = mid - 1;
        else
            loLo = mid;
    }
    const lo = loLo; // may be -1
    return { lo, hi };
}
function arrangeLocalLines(localLines, epsilon = 1e-6) {
    let xLines = [];
    let yLines = [];
    for (let line of localLines) {
        if (Math.abs(line[1][0] - line[0][0]) < epsilon)
            yLines.push(line);
        else if (Math.abs(line[1][1] - line[0][1]) < epsilon)
            xLines.push(line);
    }
    xLines.sort((a, b) => a[0][1] - b[0][1]);
    xLines.forEach((line) => {
        if (line[0][0] > line[1][0])
            line.reverse();
    });
    yLines.sort((a, b) => a[0][0] - b[0][0]);
    yLines.forEach((line) => {
        if (line[0][1] > line[1][1])
            line.reverse();
    });
    return { xLines, yLines };
}
function getAxesVals(lines, dim) {
    // Get all values, lines are sorted
    if (lines.length == 0)
        return [];
    const epsilon = 1e-3;
    const d = dim === "x" ? 0 : 1;
    let axesVals = [lines[0][0][d]];
    for (let line of lines) {
        if (line[0][d] - axesVals[axesVals.length - 1] > epsilon) {
            axesVals.push(line[0][d]);
        }
    }
    return axesVals;
}
function normalize(vector) {
    const len = Math.sqrt(scalarProd(vector, vector));
    return [vector[0] / len, vector[1] / len];
}
function getGridAxes(lines) {
    // Use longest line
    const vects = lines.map((line) => [line[1][0] - line[0][0], line[1][1] - line[0][1]]);
    const longest = vects.reduce(returnLongest, [0, 0]);
    const xAxis = normalize(longest);
    const yAxis = [xAxis[1], -xAxis[0]];
    return [xAxis, yAxis];
}
function edgesAndInteriorLines(lines) {
    // Boundary lines chain together endpoint-to-endpoint (each endpoint is shared with exactly
    // one other line's endpoint). Interior grid separators do not share endpoints with anything —
    // their endpoints lie on the boundary interior, not at polygon vertices.
    const epsilon = 0.01;
    const endpointShared = (p, i) => {
        for (let j = 0; j < lines.length; j++) {
            if (j === i)
                continue;
            const dx0 = lines[j][0][0] - p[0], dy0 = lines[j][0][1] - p[1];
            const dx1 = lines[j][1][0] - p[0], dy1 = lines[j][1][1] - p[1];
            if (dx0 * dx0 + dy0 * dy0 < epsilon * epsilon)
                return true;
            if (dx1 * dx1 + dy1 * dy1 < epsilon * epsilon)
                return true;
        }
        return false;
    };
    let edgeLines = [];
    let interiorLines = [];
    for (let i = 0; i < lines.length; i++) {
        if (endpointShared(lines[i][0], i) && endpointShared(lines[i][1], i)) {
            edgeLines.push(lines[i]);
        }
        else {
            interiorLines.push(lines[i]);
        }
    }
    return { edgeLines, interiorLines };
}
function toLocal(coordinates, xAxis, yAxis) {
    return coordinates.map((array) => array.map((vert) => project(vert, xAxis, yAxis)));
}
export const getPolygonCoordinateSystem = (polygon) => {
    const segments = polygon.slice(0, -1).map((p, i) => [p, polygon[i + 1]]);
    const [xAxis, yAxis] = getGridAxes(segments);
    const projX = polygon.map((vert) => scalarProd(vert, xAxis));
    const projY = polygon.map((vert) => scalarProd(vert, yAxis));
    const originLocal = [Math.min(...projX), Math.min(...projY)];
    const furthest = [Math.max(...projX), Math.max(...projY)];
    const origin = transform(originLocal, xAxis, yAxis);
    let polyBox = {
        xAxis: xAxis,
        yAxis: yAxis,
        origin: origin,
        localMax: vectorSubtract(furthest, originLocal),
    };
    return polyBox;
};
export const getLocalPolygon = (polygon, polyBox) => {
    const localPolygon = polygon
        .map((vert) => vectorSubtract(vert, polyBox.origin))
        .map((vert) => project(vert, polyBox.xAxis, polyBox.yAxis));
    return localPolygon;
};
export const getGlobalPolygon = (localPolygon, polyBox) => {
    const polygon = localPolygon
        .map((vert) => transform(vert, polyBox.xAxis, polyBox.yAxis))
        .map((vert) => vectorAdd(vert, polyBox.origin));
    return polygon;
};
export const evaluateOverlap = (polyBox, polygon1, polygon2) => {
    let sharedArea = 0;
    let totalArea = 0;
    const stepDistance = polyBox.localMax[0] / 100;
    for (let x = 0; x < polyBox.localMax[0]; x += stepDistance) {
        const overlap = overlapAlong(x, 1, polygon1, polygon2);
        sharedArea += overlap[0] * stepDistance;
        totalArea += overlap[1] * stepDistance;
    }
    return sharedArea / totalArea;
};
export const calculateBestPolygonOverlap = (sourcePolygon, targetPolygon) => {
    if (!sourcePolygon || !targetPolygon || sourcePolygon.length < 4 || targetPolygon.length < 4) {
        return null;
    }
    let sourceBox = getPolygonCoordinateSystem(sourcePolygon);
    let targetBox = getPolygonCoordinateSystem(targetPolygon);
    let bestOverlap = 0;
    for (const _mirror of [0, 1]) {
        for (let targetRot = 0; targetRot < 4; targetRot += 1) {
            const targetLocalPolygon = getLocalPolygon(targetPolygon, targetBox);
            for (let sourceRot = 0; sourceRot < 4; sourceRot += 1) {
                const sourceLocalPolygon = getLocalPolygon(sourcePolygon, sourceBox);
                const sharedBox = {
                    ...targetBox,
                    localMax: targetBox.localMax.map((value, index) => Math.max(value, sourceBox.localMax[index] ?? 0)),
                };
                const overlap = evaluateOverlap(sharedBox, sourceLocalPolygon, targetLocalPolygon);
                if (overlap > bestOverlap) {
                    bestOverlap = overlap;
                }
                sourceBox = rotatePolygonBox90Deg(sourceBox);
            }
            targetBox = rotatePolygonBox90Deg(targetBox);
        }
        sourceBox = mirrorPolygonBox(sourceBox);
    }
    return bestOverlap;
};
export const overlapAlong = (origin, dim, polygon0, polygon1) => {
    const allSides0 = polygon0.map((vert, i, pol) => [vert, pol[(i + 1) % pol.length]]);
    const allSides1 = polygon1.map((vert, i, pol) => [vert, pol[(i + 1) % pol.length]]);
    allSides0
        .filter((side) => side[0][1 - dim] - origin !== side[1][1 - dim] - origin)
        .filter((side) => (side[0][1 - dim] - origin) * (side[1][1 - dim] - origin) <= 0);
    const intersect0 = allSides0
        .filter((side) => side[0][1 - dim] - origin !== side[1][1 - dim] - origin)
        .filter((side) => (side[0][1 - dim] - origin) * (side[1][1 - dim] - origin) <= 0)
        .map((side) => side[0][dim] +
        ((origin - side[0][1 - dim]) * (side[1][dim] - side[0][dim])) /
            (side[1][1 - dim] - side[0][1 - dim]))
        .map((side) => [0, side]);
    const intersect1 = allSides1
        .filter((side) => side[0][1 - dim] - origin !== side[1][1 - dim] - origin)
        .filter((side) => (side[0][1 - dim] - origin) * (side[1][1 - dim] - origin) <= 0)
        .map((side) => side[0][dim] +
        ((origin - side[0][1 - dim]) * (side[1][dim] - side[0][dim])) /
            (side[1][1 - dim] - side[0][1 - dim]))
        .map((side) => [1, side]);
    const allIntersects = [...intersect0, ...intersect1];
    const intersects = allIntersects.sort((a, b) => a[1] - b[1]);
    let nonSharedLength = 0;
    let sharedLength = 0;
    let inside = [false, false];
    let pos = 0;
    for (let intersect of intersects) {
        if (inside[0] && inside[1]) {
            sharedLength += intersect[1] - pos;
        }
        else if (inside[0] || inside[1]) {
            nonSharedLength += intersect[1] - pos;
        }
        inside[intersect[0]] = !inside[intersect[0]];
        pos = intersect[1];
    }
    return [sharedLength, nonSharedLength + sharedLength];
};
export const rotatePolygonBox90Deg = (polyBox) => {
    const rotatedPolyBox = {
        xAxis: polyBox.yAxis,
        yAxis: [-polyBox.xAxis[0], -polyBox.xAxis[1]],
        origin: vectorAdd(polyBox.origin, transform([polyBox.localMax[0], 0], polyBox.xAxis, [0, 0])),
        localMax: [polyBox.localMax[1], polyBox.localMax[0]],
    };
    return rotatedPolyBox;
};
export const mirrorPolygonBox = (polyBox) => {
    const mirroredPolyBox = {
        xAxis: polyBox.yAxis,
        yAxis: polyBox.xAxis,
        origin: polyBox.origin,
        localMax: [polyBox.localMax[1], polyBox.localMax[0]],
    };
    return mirroredPolyBox;
};
export const transformCoordinateUsingSystems = (coordinate, source, destination) => {
    const localSource = project(vectorSubtract(coordinate, source.origin), source.xAxis, source.yAxis);
    return vectorAdd(transform(localSource, destination.xAxis, destination.yAxis), destination.origin);
};
export const transformDirectionUsingSystems = (direction, source, destination) => {
    const localDirection = project(direction, source.xAxis, source.yAxis);
    const globalDirection = transform(localDirection, destination.xAxis, destination.yAxis);
    return globalDirection;
};
export function parseAllCeilingGrids(ceilingGrids) {
    const allTiles = ceilingGrids.map((grid) => {
        const { edgeLines, interiorLines } = edgesAndInteriorLines(grid.lines);
        const subgrid = parseOneCeilingGrid(edgeLines, interiorLines);
        return subgrid.tiles;
    });
    return allTiles;
}
export const calculateCeilingIntersection = (ceilingGrids) => {
    //TODO: Implement this function to calculate the intersection of ceiling grids and return the appropriate coordinates and direction for snapping devices to the ceiling grid. This will likely involve finding the closest ceiling grid to a given point, determining the orientation of that grid, and calculating the intersection point on the grid where a device should be snapped.
    let largestArea = 0;
    let largestGridLines = null;
    for (let grid of ceilingGrids) {
        const { edgeLines, interiorLines } = edgesAndInteriorLines(grid.lines);
        const areaApproximation = interiorLines.reduce((acc, line) => {
            const length = Math.sqrt(scalarProd(vectorSubtract(line[1], line[0]), vectorSubtract(line[1], line[0])));
            return acc + length;
        }, 0);
        if (areaApproximation > largestArea) {
            largestArea = areaApproximation;
            largestGridLines = interiorLines;
        }
    }
    if (largestGridLines == null) {
        return {
            x: 0,
            y: 0,
            direction: {
                x: 0,
                y: 0,
            },
        };
    }
    const [xAxis, yAxis] = getGridAxes(largestGridLines);
    let xLine = null;
    let yLine = null;
    for (let line of largestGridLines) {
        const vector = vectorSubtract(line[1], line[0]);
        const scalarProdX = scalarProd(xAxis, vector);
        if (Math.abs(scalarProdX) > 1 - 1e-3) {
            xLine = line;
        }
        else if (scalarProdX < 1e-3) {
            yLine = line;
        }
        if (xLine != null && yLine != null) {
            break;
        }
    }
    let intersectionPoint = largestGridLines[0][0];
    if (xLine != null && yLine != null) {
        const yVal = scalarProd(yAxis, xLine[0]);
        const xVal = scalarProd(xAxis, yLine[0]);
        const [x, y] = transform([xVal, yVal], xAxis, yAxis);
        intersectionPoint = [x, y];
    }
    const output = {
        x: intersectionPoint[0],
        y: intersectionPoint[1],
        direction: {
            x: xAxis[0],
            y: xAxis[1],
        },
    };
    return output;
};
